import RefreshIcon from "@assets/icons/refresh.svg";
import LightCustomButton from "@components/buttons/LightCustomButton";
import TabSwitcherWeb from "@components/switcher/TabSwitcherWeb";
import { primeServices } from "@services/apis/Prime";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  isPaysonicHost,
  isPrimeBrazilOnlyHost,
} from "src/config/deploymentConfig";
import BrNtxRatesSlider from "./components/BrNtxRatesSlider";
import BrPixServiceStatus from "./components/BrPixServiceStatus";
import InUpiServiceStatus from "./components/InUpiServiceStatus";
import CadInternalTransferPanel from "./components/CadInternalTransferPanel";
import CadManualPayoutPanel from "./components/CadManualPayoutPanel";
import ExpireNtxRampTradesSlider from "./components/ExpireNtxRampTradesSlider";
import NtxCircuitBreakerPanel from "./components/NtxCircuitBreakerPanel";
import ExtendNtxDeadlinesSlider from "./components/ExtendNtxDeadlinesSlider";
import PrimeOrderFlow from "./PrimeOrderFlow";
import PrimeOrderFlowCanada from "./PrimeOrderFlowCanada";
import PrimeOrderFlowEurope from "./PrimeOrderFlowEurope";
import PrimeOrderFlowPhilippines from "./PrimeOrderFlowPhilippines";
import ConstantFunctions from "../../utils/constants_functions";

const BRAZIL_TAB_INDEX = 1;
const INDIA_TAB_INDEX = 0;

const PrimeOrderFlowTabs = () => {
  const isPrimeBrazilOnly = isPrimeBrazilOnlyHost();
  const isPaysonic = isPaysonicHost();
  const constantFunctions = new ConstantFunctions();
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [isInternalTransferOpen, setIsInternalTransferOpen] = useState(false);
  const [isManualPayoutOpen, setIsManualPayoutOpen] = useState(false);
  const [isExtendPanelOpen, setIsExtendPanelOpen] = useState(false);
  const [isExpirePanelOpen, setIsExpirePanelOpen] = useState(false);
  const [isNtxMonitorOpen, setIsNtxMonitorOpen] = useState(false);
  const [isRatesPanelOpen, setIsRatesPanelOpen] = useState(false);
  const [ratesVersion, setRatesVersion] = useState(0);
  const [ntxBalance, setNtxBalance] = useState<number | null>(null);
  const [ntxBalanceLoading, setNtxBalanceLoading] = useState(false);
  const [ntxBalanceReloadKey, setNtxBalanceReloadKey] = useState(0);
  const navigate = useNavigate();

  const allTabs = useMemo(
    () => [
      {
        label: "India",
        component: <PrimeOrderFlow key="IN" countryFilter="IN" />,
      },
      {
        label: "Brazil",
        component: <PrimeOrderFlow key="BR" countryFilter="BR" />,
      },
      {
        label: "Europe",
        component: <PrimeOrderFlowEurope />,
      },
      {
        label: "Philippines",
        component: <PrimeOrderFlowPhilippines />,
      },
      {
        label: "Canada",
        component: <PrimeOrderFlowCanada />,
      },
    ],
    [],
  );

  const tabs = isPrimeBrazilOnly
    ? allTabs.filter((tab) => tab.label === "Brazil")
    : isPaysonic
      ? allTabs.filter((tab) => tab.label === "India")
      : allTabs;

  const showBrPixStatus =
    isPrimeBrazilOnly || activeTabIndex === BRAZIL_TAB_INDEX;

  const showPrimeIndiaVPNSettings =
    !isPrimeBrazilOnly && (isPaysonic || activeTabIndex === INDIA_TAB_INDEX);

  const showCadInternalTransfer =
    !isPrimeBrazilOnly &&
    !isPaysonic &&
    tabs[activeTabIndex]?.label === "Canada";

  useEffect(() => {
    if (!showBrPixStatus) return;

    let cancelled = false;
    let inFlight = false;

    const loadNtxBalance = async (silent = false) => {
      if (inFlight) return;
      inFlight = true;
      if (!silent) {
        setNtxBalanceLoading(true);
      }
      try {
        const response = await primeServices.getNtxBalance({ silent });
        if (!cancelled) {
          setNtxBalance(response.data.netAmount);
        }
      } catch {
        if (!cancelled && !silent) {
          setNtxBalance(null);
        }
      } finally {
        inFlight = false;
        if (!cancelled && !silent) {
          setNtxBalanceLoading(false);
        }
      }
    };

    loadNtxBalance();
    const intervalId = window.setInterval(() => {
      loadNtxBalance(true);
    }, 5000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [showBrPixStatus, ntxBalanceReloadKey]);

  return (
    <div className="flex flex-col h-full">
      <div className="h-[70px] border-b border-border-subtle bg-bg-page-body flex flex-row sticky top-0 bg-white z-10 pl-[20px]">
        <div className="flex flex-row justify-between items-center w-full mr-4 gap-4">
          <p className="text-text-base text-[14px] font-semibold shrink-0">
            Cross Ramp Order Flow
          </p>
          <div className="flex items-center gap-2 min-w-0 overflow-x-auto">
            {showBrPixStatus && (
              <div className="flex items-center gap-1.5 border border-border-subtle rounded-md px-2 py-1 bg-bg-page-body shrink-0">
                <span className="text-text-subtle text-[11px] font-semibold whitespace-nowrap">
                 BRL Balance:
                </span>
                <span className="text-text-base text-[11px] font-semibold whitespace-nowrap">
                  {ntxBalanceLoading && ntxBalance === null
                    ? "..."
                    : ntxBalance === null
                      ? "--"
                      : constantFunctions.formatCurrency(ntxBalance)}
                </span>
                <button
                  type="button"
                  title="Reload NTX BRL balance"
                  aria-label="Reload NTX BRL balance"
                  disabled={ntxBalanceLoading}
                  onClick={() => setNtxBalanceReloadKey((key) => key + 1)}
                  className="flex items-center justify-center disabled:opacity-50"
                >
                  <img
                    src={RefreshIcon}
                    alt=""
                    className={ntxBalanceLoading ? "animate-spin" : ""}
                  />
                </button>
              </div>
            )}
            {showBrPixStatus && (
              <BrPixServiceStatus
                onOpenRates={() => setIsRatesPanelOpen(true)}
                ratesVersion={ratesVersion}
              />
            )}
            {showPrimeIndiaVPNSettings && <InUpiServiceStatus />}
            {showPrimeIndiaVPNSettings && (
              <LightCustomButton
                text="VPN Settings"
                isActive={true}
                onClick={() => navigate("/prime-order-flow/vpn-settings")}
                textSize="12px"
                className="min-w-[84px] shrink-0"
              />
            )}
            {showBrPixStatus && (
              <LightCustomButton
                text="EXTEND NTX"
                onClick={() => setIsExtendPanelOpen(true)}
                isActive={true}
                textSize="12px"
                className="min-w-[84px] shrink-0"
              />
            )}
            {showBrPixStatus && (
              <LightCustomButton
                text="EXPIRE NTX"
                onClick={() => setIsExpirePanelOpen(true)}
                isActive={true}
                textSize="12px"
                className="min-w-[84px] shrink-0"
              />
            )}
            {showBrPixStatus && (
              <LightCustomButton
                text="NTX Monitor"
                onClick={() => setIsNtxMonitorOpen(true)}
                isActive={true}
                textSize="12px"
                className="min-w-[96px] shrink-0"
              />
            )}
            {showCadInternalTransfer && (
              <LightCustomButton
                text="Internal Transfer"
                isActive={true}
                onClick={() => setIsInternalTransferOpen(true)}
                textSize="12px"
                className="min-w-[120px] shrink-0"
              />
            )}
            {showCadInternalTransfer && (
              <LightCustomButton
                text="Manual Payout"
                isActive={true}
                onClick={() => setIsManualPayoutOpen(true)}
                textSize="12px"
                className="min-w-[120px] shrink-0"
              />
            )}
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-hidden mt-4 mx-4">
        <TabSwitcherWeb
          tabs={tabs}
          full={true}
          onTabChange={setActiveTabIndex}
        />
      </div>
      <CadInternalTransferPanel
        isOpen={isInternalTransferOpen}
        onClose={() => setIsInternalTransferOpen(false)}
      />
      <CadManualPayoutPanel
        isOpen={isManualPayoutOpen}
        onClose={() => setIsManualPayoutOpen(false)}
      />
      <ExtendNtxDeadlinesSlider
        isOpen={isExtendPanelOpen}
        onClose={() => setIsExtendPanelOpen(false)}
      />
      <ExpireNtxRampTradesSlider
        isOpen={isExpirePanelOpen}
        onClose={() => setIsExpirePanelOpen(false)}
      />
      <NtxCircuitBreakerPanel
        isOpen={isNtxMonitorOpen}
        onClose={() => setIsNtxMonitorOpen(false)}
      />
      <BrNtxRatesSlider
        isOpen={isRatesPanelOpen}
        onClose={() => setIsRatesPanelOpen(false)}
        onSaved={() => setRatesVersion((version) => version + 1)}
      />
    </div>
  );
};

export default PrimeOrderFlowTabs;
